import { Component, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';

import { LangPipe } from '@reforgium/presentia';
import { SkToastComponent } from '@ss/ui';

import { HeaderComponent } from '../header';

@Component({
  selector: 'app-main-layout',
  host: { '[class.guest-layout]': 'isGuestRoute()' },
  imports: [HeaderComponent, RouterOutlet, SkToastComponent, LangPipe],
  template: `
    <div class="page-container">
      <sk-toast />
      @if (isGuestRoute()) {
        <a class="skip-link" href="#main-content" (click)="skipToContent($event, pageScroll)">
          {{ 'common.accessibility.skipContent' | lang }}
        </a>
      }
      <app-header class="print:hidden" />

      <main #pageScroll id="main-content" tabindex="-1" class="content-container">
        <div class="content">
          <router-outlet (activate)="pageScroll.scrollTo({ top: 0, left: 0 })" />
        </div>
      </main>
    </div>
  `,
  styles: `
    .skip-link {
      position: fixed;
      top: 12px;
      left: 16px;
      z-index: 1102;
      padding: 12px 16px;
      border: 2px solid currentColor;
      border-radius: 8px;
      background: var(--surface-neutral);
      color: var(--text-primary);
      transform: translateY(-200%);
    }
    .skip-link:focus {
      transform: none;
    }
    .content-container:focus {
      outline: 2px solid var(--text-primary);
      outline-offset: -2px;
    }
    @media print {
      .skip-link {
        display: none;
      }
    }
    .page-container {
      display: flex;
      flex-direction: column;
      height: 100dvh;
      width: 100%;
      overflow: hidden;

      @media screen and (max-width: 768px) {
        box-sizing: border-box;
        padding-bottom: calc(64px + env(safe-area-inset-bottom));
        background: var(--surface-background);
      }

      @media print {
        height: auto;
        overflow: visible;
      }
    }

    .content-container {
      position: relative;
      width: 100%;
      flex: 1 1 auto;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      z-index: 1;

      @media print {
        overflow: visible;
      }
    }

    .content::before {
      content: '';
      position: fixed;
      inset: 126px 8% 0;
      background: url('assets/logo/logo.svg') center top / min(1100px, 100%) auto no-repeat;
      filter: brightness(0) saturate(100%);
      opacity: 0.025;
      pointer-events: none;
      z-index: -1;
    }

    .content {
      padding: 24px 4.2%;
      margin-bottom: 64px;
      min-height: 100%;
      position: relative;
      isolation: isolate;
      background-color: var(--surface-background);

      @media screen and (max-width: 1400px) {
        padding-inline: 4.2%;
      }

      @media screen and (max-width: 768px) {
        padding-inline: 16px;
      }

      @media print {
        padding: 0;
        background-color: transparent;
      }
    }
  `,
})
export class MainLayoutComponent {
  private readonly router = inject(Router);

  protected isGuestRoute(): boolean {
    let route = this.router.routerState.snapshot.root;

    while (route.firstChild) route = route.firstChild;

    return route.data['accessibilitySpeech'] === true;
  }

  protected skipToContent(event: Event, main: HTMLElement): void {
    event.preventDefault();
    main.focus({ preventScroll: true });
    main.scrollTo({ top: 0, left: 0 });
  }
}
